import { StyleSheet } from "react-native";
import LBox from "@components/LBox";
import LText from "@components/LText";
import QRCode from "react-native-qrcode-svg";
import type { Collect } from "@libs/platform-wire";
import { createVendorTicketsModel } from "@libs/shared-ui";
import { TicketCarousel } from "./TicketCarousel";

/**
 * My Tickets for a purchase bought on a seller's site: only the tickets, as
 * the read's `collect` says them through `createVendorTicketsModel` (shared
 * with Link in Bio's tickets page). While the seller holds an order back,
 * when it will be available; once released, the seller's own codes in a
 * carousel like engine tickets'; otherwise where the tickets are. How it
 * works, the references and the seller account are behind the sheet's "?"
 * (VendorTicketsHelpSheet).
 */
export function VendorTickets({
  collect,
  width,
  boxWidth,
}: {
  collect: Collect;
  /** The carousel's page width and the code box's side, as engine tickets'. */
  width: number;
  boxWidth: number;
}) {
  const { notices, codes } = createVendorTicketsModel(collect);

  return (
    <LBox gap="s3" testID="vendor-tickets">
      {notices.map((notice) => (
        <Notice
          key={notice.key}
          testID={notice.testID}
          title={notice.title}
          body={notice.body}
        />
      ))}

      {codes.length ? (
        <TicketCarousel
          width={width}
          boxWidth={boxWidth}
          pages={codes.map((code) => ({
            key: code.key,
            box: <VendorCode code={code} size={boxWidth} />,
            caption: (
              <LBox alignItems="center" gap="s1" pt="s1">
                <LText variant="Body/B1/Medium">{code.label}</LText>
                {code.numberLine ? (
                  <LText variant="Body/B2/Medium" color="whiteTranslucent60">
                    {code.numberLine}
                  </LText>
                ) : null}
              </LBox>
            ),
          }))}
        />
      ) : null}
    </LBox>
  );
}

function Notice({
  title,
  body,
  testID,
}: {
  title: string;
  body: string;
  testID: string;
}) {
  return (
    <LBox style={styles.card} gap="s1" testID={testID}>
      <LText variant="Heading/H2/SemiBold">{title}</LText>
      <LText variant="Body/B2/Medium" color="whiteTranslucent60">
        {body}
      </LText>
    </LBox>
  );
}

/** The seller's code, exactly as it states it: a QR code for QR_CODE. Other
 *  barcode types are shown as their value, large, with the type: no barcode
 *  renderer ships in the app (adding one is a native change). */
function VendorCode({
  code,
  size,
}: {
  code: { isQr: boolean; codeType: string; code: string };
  size: number;
}) {
  return (
    <LBox
      bg="white"
      borderRadius="m"
      width={size}
      height={size}
      overflow="hidden"
      alignItems="center"
      justifyContent="center"
      testID="vendor-ticket-code"
    >
      {code.isQr ? (
        <QRCode size={size - 20} value={code.code} />
      ) : (
        <LBox alignItems="center" gap="s2" px="s2">
          <LText
            variant="h2"
            color="black"
            textAlign="center"
            selectable
            adjustsFontSizeToFit
            numberOfLines={2}
          >
            {code.code}
          </LText>
          <LText variant="Body/B2/Medium" color="black" textAlign="center">
            {code.codeType}
          </LText>
        </LBox>
      )}
    </LBox>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    borderRadius: 16,
    padding: 16,
  },
});
